import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Share2, Copy, Check, AlertTriangle, Pencil,
} from "lucide-react";
import { Button, Toolbar, SegmentedControl, MicroLabel, Card, Chip }
  from "@/components/wyvio-ui";
import { NextActionCard } from "@/components/wyvio-ui";
import DossierHeroV2 from "@/components/dossier/DossierHeroV2";
import StepDetailSheet from "@/components/dossier/StepDetailSheet";
import PaymentActionSheet from "@/components/dossier/PaymentActionSheet";
import DeliverySlotsSheet from "@/components/dossier/DeliverySlotsSheet";
import TabMessages from "@/components/dossier/tabs/TabMessages";
import TabDocumentsV2 from "@/components/dossier/tabs/TabDocumentsV2";
import SavSection from "@/components/dossier/SavSection";
import DossierForm from "@/components/dossier/DossierForm";
import ClientAccountCard from "@/components/dossier/ClientAccountCard";
import { resolveNextAction } from "@/lib/nextAction";
import api from "@/lib/api";
import { normalizeDetail } from "@/lib/normalize";
import { STEP_STATUS } from "@/data/mockData";

const TABS = [
  { key: "suivi",     label: "Suivi" },
  { key: "messages",  label: "Messages" },
  { key: "documents", label: "Documents" },
];

const DossierDetail = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [activeTab, setActiveTab] = useState("suivi");
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [paymentSheet, setPaymentSheet] = useState(null); // "confirm" | "remind" | null
  const [deliverySheet, setDeliverySheet] = useState(false);
  const [delivery, setDelivery] = useState(null);

  // ── Chargement ───────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [d, p, dv] = await Promise.all([
          api.getDossier(id),
          api.getPayment(id).catch(() => null),
          api.getDelivery(id).catch(() => null),
        ]);
        if (!alive) return;
        setVehicle(normalizeDetail(d));
        setPayment(p);
        setDelivery(dv?.delivery || null);
        setError(null);
      } catch (e) {
        if (alive) setError(e.message || "Erreur de chargement");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const refresh = useCallback(async () => {
    const [d, p, dv] = await Promise.all([
      api.getDossier(id),
      api.getPayment(id).catch(() => null),
      api.getDelivery(id).catch(() => null),
    ]);
    setVehicle(normalizeDetail(d));
    setPayment(p);
    setDelivery(dv?.delivery || null);
  }, [id]);

  // ── Actions atomiques ────────────────────────────────────────────────
  const handleCopy = async () => {
    try {
      const { path } = await api.createMagicLink(id);
      const url = `${window.location.origin}${path}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyError(false);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopyError(true);
      setTimeout(() => setCopyError(false), 1800);
    }
  };

  const openClientView = async () => {
    try {
      const { path } = await api.createMagicLink(id);
      window.open(path, "_blank", "noopener");
    } catch { /* silencieux */ }
  };

  const handleSendMessage = async (text) => {
    if (!text?.trim()) return;
    await api.sendAgencyMessage(id, text.trim());
    await refresh();
  };

  const handleUpdateStep = async (stepKey, patch) => {
    const updated = await api.updateStep(id, stepKey, patch);
    setVehicle(normalizeDetail(updated));
    setActiveStep(null);
    // Rafraîchir le paiement (si l'étape terminée débloquait la livraison)
    api.getPayment(id).then(setPayment).catch(() => {});
  };

  // ── Cerveau : détermine la prochaine action ──────────────────────────
  const nextAction = useMemo(() => {
    if (!vehicle) return null;
    return resolveNextAction({
      vehicle,
      payment,
      delivery,
      actions: {
        openStep: (s) => setActiveStep(s),
        openPaymentConfirm: () => setPaymentSheet("confirm"),
        openPaymentRemind: () => setPaymentSheet("remind"),
        openEdit: () => setShowEdit(true),
        openMessagesTab: () => setActiveTab("messages"),
        openDeliverySlots: () => setDeliverySheet(true),
      },
    });
  }, [vehicle, payment, delivery]);

  // ── Loading skeleton ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div data-testid="dossier-loading" className="wyvio-fade-in">
        <div className="rounded-3xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] overflow-hidden animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-[520px_1fr]">
            <div className="aspect-[5/4] lg:aspect-auto lg:min-h-[380px] bg-[#EEF2F5] dark:bg-[#1A1A1A]" />
            <div className="p-10 space-y-5">
              <div className="h-3 w-24 bg-[#EEF2F5] dark:bg-[#1A1A1A] rounded" />
              <div className="h-12 w-2/3 bg-[#EEF2F5] dark:bg-[#1A1A1A] rounded" />
              <div className="h-3 w-1/2 bg-[#EEF2F5] dark:bg-[#1A1A1A] rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <Card className="p-8" data-testid="dossier-error">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center">
            <AlertTriangle size={17} strokeWidth={2} />
          </div>
          <div>
            <p className="text-[14px] font-semibold text-[#0A0A0A] dark:text-white">
              Dossier introuvable
            </p>
            <p className="text-[12.5px] text-[#6B7280] dark:text-[#A3A3A3]">
              {error || "Ce dossier n'existe pas ou n'est plus accessible."}
            </p>
          </div>
        </div>
        <Link to="/app" className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0A0A0A] dark:text-white">
          <ArrowLeft size={14} strokeWidth={2} /> Retour au dashboard
        </Link>
      </Card>
    );
  }

  const messagesCount = vehicle.messages?.length || 0;
  const blockedCount = (vehicle.steps || [])
    .filter((s) => s.status === STEP_STATUS.BLOCKED).length;

  return (
    <div className="wyvio-fade-in" data-testid="page-dossier-detail">
      {/* Toolbar unique */}
      <Toolbar
        className="mb-8"
        left={
          <Link
            to="/app"
            data-testid="back-to-dashboard"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#6B7280] dark:text-[#A3A3A3] hover:text-[#0A0A0A] dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={15} strokeWidth={2} />
            Dashboard
          </Link>
        }
        right={
          <>
            <Button
              icon={Pencil}
              onClick={() => setShowEdit(true)}
              data-testid="edit-dossier-btn"
            >
              Éditer
            </Button>
            <Button
              icon={copyError ? AlertTriangle : copied ? Check : Copy}
              onClick={handleCopy}
              data-testid="copy-client-link"
            >
              {copyError ? "Erreur" : copied ? "Lien copié" : "Lien client"}
            </Button>
            <Button
              variant="dark"
              icon={Share2}
              onClick={openClientView}
              data-testid="open-client-view"
            >
              Vue client
            </Button>
          </>
        }
      />

      {/* Hero identité véhicule */}
      <DossierHeroV2 vehicle={vehicle} onStepClick={setActiveStep} />

      {/* Cockpit : la prochaine action */}
      {nextAction && (
        <div className="mt-6">
          <NextActionCard {...nextAction} />
        </div>
      )}

      {/* Tabs pill — Suivi / Messages / Documents */}
      <div className="mt-10 flex justify-center">
        <SegmentedControl
          testId="dossier-tabs"
          value={activeTab}
          onChange={setActiveTab}
          options={TABS.map((t) => ({
            key: t.key,
            label:
              t.key === "messages" && messagesCount > 0
                ? `${t.label} · ${messagesCount}`
                : t.label,
          }))}
        />
      </div>

      <div className="mt-6">
        {activeTab === "suivi" && (
          <SuiviTab
            vehicle={vehicle}
            blockedCount={blockedCount}
            onOpenStep={setActiveStep}
            dossierId={id}
          />
        )}
        {activeTab === "messages" && (
          <TabMessages
            messages={vehicle.messages || []}
            client={vehicle.client}
            readOnly={vehicle.isFinished}
            onSend={handleSendMessage}
          />
        )}
        {activeTab === "documents" && (
          <TabDocumentsV2
            dossierId={id}
            documents={vehicle.documents || []}
            media={vehicle.media || []}
            readOnly={vehicle.isFinished}
            onChange={refresh}
          />
        )}
      </div>

      {/* SAV — n'apparaît qu'une fois le dossier terminé */}
      {vehicle.isFinished && (
        <div className="mt-10">
          <SavSection dossierId={id} eligible={true} />
        </div>
      )}

      {/* Sheets & modals */}
      <StepDetailSheet
        step={activeStep}
        allSteps={vehicle.steps || []}
        onClose={() => setActiveStep(null)}
        onUpdate={(patch) => handleUpdateStep(activeStep.key, patch)}
        readOnly={vehicle.isFinished}
      />

      {paymentSheet && (
        <PaymentActionSheet
          mode={paymentSheet}
          dossierId={id}
          initial={payment?.summary}
          onClose={() => setPaymentSheet(null)}
          onDone={async () => {
            setPaymentSheet(null);
            await refresh();
          }}
        />
      )}

      {deliverySheet && (
        <DeliverySlotsSheet
          dossierId={id}
          current={delivery}
          defaultLocation={vehicle.agencyAddress || vehicle.deliveryLocation || ""}
          onClose={() => setDeliverySheet(false)}
          onSaved={async () => {
            setDeliverySheet(false);
            await refresh();
          }}
        />
      )}

      {showEdit && (
        <DossierForm
          mode="edit"
          initial={{
            id: vehicle.id,
            client: vehicle.client,
            clientEmail: vehicle.clientEmail,
            clientPhone: vehicle.clientPhone,
            name: vehicle.name,
            image: vehicle.image,
            orderNumber: vehicle.orderNumber,
            availabilityRaw: vehicle.availabilityDateRaw,
            deliveryDate: vehicle.deliveryDateRaw,
            deliveryTime: vehicle.deliveryTime,
            deliveryLocation: vehicle.deliveryLocation,
            commercialId: vehicle.commercialId,
          }}
          onClose={() => setShowEdit(false)}
          onSaved={async () => { await refresh(); }}
        />
      )}

      <div className="h-20" />
    </div>
  );
};

/**
 * SuiviTab — remplace TabSuivi V1.
 *
 * Répond à « où en est la vente ? » sans dupliquer la prochaine action
 * (déjà affichée dans le cockpit) : timeline verticale claire des étapes
 * + accès client (fusion de l'ancienne ClientAccountCard).
 */
const SuiviTab = ({ vehicle, blockedCount, onOpenStep, dossierId }) => {
  const steps = vehicle.steps || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6" data-testid="tab-content-suivi">
      {/* Timeline verticale */}
      <Card className="p-6 lg:p-7">
        <div className="flex items-center justify-between">
          <MicroLabel>Parcours du dossier</MicroLabel>
          {blockedCount > 0 && (
            <Chip tone="danger" size="sm" data-testid="suivi-blocked-count">
              {blockedCount} bloquée{blockedCount > 1 ? "s" : ""}
            </Chip>
          )}
        </div>
        <ol className="mt-5 space-y-1" data-testid="suivi-steps-list">
          {steps.map((step, idx) => (
            <StepRow
              key={step.key}
              step={step}
              index={idx + 1}
              isLast={idx === steps.length - 1}
              onOpen={() => onOpenStep(step)}
            />
          ))}
        </ol>
      </Card>

      {/* Accès client — utilitaire discret */}
      <div className="space-y-4">
        <ClientAccountCard
          dossierId={dossierId}
          clientAccount={vehicle.clientAccount}
          clientEmail={vehicle.clientEmail}
        />
      </div>
    </div>
  );
};

const StepRow = ({ step, index, isLast, onOpen }) => {
  const done = step.status === STEP_STATUS.DONE;
  const inProgress = step.status === STEP_STATUS.IN_PROGRESS;
  const blocked = step.status === STEP_STATUS.BLOCKED;

  const dot = blocked
    ? "bg-[#EF4444]"
    : done
    ? "bg-[#0A0A0A] dark:bg-white"
    : inProgress
    ? "bg-[#C8FF2F]"
    : "bg-[#E5E7EB] dark:bg-[#262626]";

  return (
    <li>
      <button
        onClick={onOpen}
        data-testid={`suivi-step-${step.key}`}
        className="group w-full text-left flex items-start gap-4 py-3 rounded-2xl px-2 -mx-2 hover:bg-[#F7F8FA] dark:hover:bg-[#0A0A0A] transition-colors"
      >
        <div className="relative shrink-0 mt-1">
          <span className={`block h-3 w-3 rounded-full ${dot} ${inProgress ? "animate-pulse" : ""}`} />
          {!isLast && (
            <span
              aria-hidden
              className="absolute left-1/2 -translate-x-1/2 top-4 h-8 w-px bg-[#EEF2F5] dark:bg-[#262626]"
            />
          )}
        </div>
        <div className="flex-1 min-w-0 pb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] tabular-nums font-medium text-[#9CA3AF] dark:text-[#525252]">
              {String(index).padStart(2, "0")}
            </span>
            <p
              className={[
                "text-[14px] font-semibold tracking-[-0.01em]",
                done ? "text-[#6B7280] dark:text-[#A3A3A3] line-through decoration-[#D1D5DB]"
                     : "text-[#0A0A0A] dark:text-white",
              ].join(" ")}
            >
              {step.label || step.title}
            </p>
            {inProgress && (
              <Chip tone="accent" size="sm">En cours</Chip>
            )}
            {blocked && (
              <Chip tone="danger" size="sm">Bloqué</Chip>
            )}
          </div>
          {blocked && step.blocking_reason && (
            <p className="mt-1 text-[12.5px] text-[#991B1B] dark:text-[#FCA5A5] leading-snug">
              {step.blocking_reason}
            </p>
          )}
        </div>
      </button>
    </li>
  );
};

export default DossierDetail;
